import React, { useState, useEffect, useCallback, useRef, ChangeEvent } from 'react'
import { useParams, useOutletContext } from 'react-router-dom'
import axios from 'axios'
import { ChevronDown, ChevronUp, Info, Plus } from 'lucide-react'
import {
  Card,
  CollapsibleHeader,
  Separator,
  Title,
  Button,
  AddButton,
  AddButtonImg,
  AddButtonText,
  AddProductContainer,
  FormGroup,
  Label,
  InputText,
  Select,
  FormGrid,
  BreadCrumble,
  FirstText,
  SecondText,
  InfoLink,
  Summary,
  SummaryItem,
  SummaryLabel,
  SummaryValue,
  ProductShort,
  ProductDiv,
  ShortLabel,
  ActionsDiv,
  FileUploadContainer,
  FileUploadLabel,
  FileNameText,
} from './style'
import AddProductForm from '../AddProductForm'
import CompanySelect from '../SelectItem/CompanySelect'
import VendorSelect from '../SelectItem/VendorSelect'
import plus from '../../../../assets/fashion/TableFashionHeader/plus.svg'
import rectangle from '../../../../assets/fashion/TableFashionHeader/rectangle.svg'
import trash from '../../../../assets/fashion/TableFashionHeader/trash.svg'
import cross from '../../../../assets/produtcss/cross.svg'

interface FashionEditContent {
  handleEditSave: React.MutableRefObject<() => Promise<void>>
}

type SizeQuantities = { [size: string]: number }

interface FashionOrderProductItemInput {
  colorId: number;
  size: string;
  quantity: number;
}

interface ProductApiData {
  fashionProductId?: number
  name?: string
  totalItems?: number
  subtotal?: number
  discount?: number
  colorCount?: number
  items?: FashionOrderProductItemInput[]
  fashionProduct?: {
    id: number
    name: string
    price: number
    group?: {
      id: number
      name: string
      family?: {
        id: number
        name: string
      }
    }
  }
  family?: { name: string }
  group?: { name: string }
  sizeQuantities?: SizeQuantities
  colors?: string[]
  selectedColors?: number[]
  discountValue?: number
}

interface CompanyItemType {
  id: number
  companyName: string
}

interface UserItemType {
  id: number
  name: string
}

interface FreightType {
  value: string
  label: string
}

interface PaymentCondition {
  name: string
}

interface Attachment {
  id: number;
  originalName: string;
  fileName: string;
  filePath: string;
  fileSize: number;
  mimeType: string;
}

interface EditFashionOrderPayload {
  orderType: string;
  trackingCode: string;
  extraInformation: string;
  deliveryTime: string;
  maximumBillingAmount: string;
  quantityItems: string;
  quantityPieces: string;
  totalCost: number;
  shippingCost: number;
  finalDateOfDelivery: string | null;
  startDateOfDelivery: string | null;
  supplier: CompanyItemType;
  seller: UserItemType;
  company: CompanyItemType;
  shippingType: string;
  paymentCondition: string;
  products: any[];
  attachmentFile?: string;
  attachmentFileName?: string;
}

const calculateDiscountValue = (subtotal: number, discountPercentage: number): number => {
  return (subtotal * discountPercentage) / 100;
}

const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = error => reject(error)
  })
}

const EditFashionOrder = () => {
  const [isDetailsOpen, setIsDetailsOpen] = useState(true)
  const [showAddProduct, setShowAddProduct] = useState(false)
  const { orderId } = useParams()
  const { handleEditSave } = useOutletContext<FashionEditContent>()
  const [addedProducts, setAddedProducts] = useState<ProductApiData[]>([])
  const [openProductIndex, setOpenProductIndex] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)

  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [fileName, setFileName] = useState<string | null>(null)
  const [fileUploadStatus, setFileUploadStatus] = useState<string>("")

  const [existingAttachments, setExistingAttachments] = useState<Attachment[]>([])

  const [formData, setFormData] = useState({
    supplier: { id: 0, companyName: '' } as CompanyItemType,
    seller: { id: 0, name: '' } as UserItemType,
    company: { id: 0, companyName: '' } as CompanyItemType,
    orderType: '',
    paymentCondition: '',
    shippingType: '',
    trackingCode: '',
    attachments: '',
    finalDateOfDelivery: '',
    startDateOfDelivery: '',
    extraInformation: '',
    quantityItems: '',
    quantityPieces: '',
    totalCost: '',
    deliveryTime: '',
    shippingCost: '',
    maximumBillingAmount: '',
  })

  const [freightTypes, setFreightTypes] = useState<FreightType[]>([])
  const [paymentOptions, setPaymentOptions] = useState<string[]>([])
  const [newPaymentCondition, setNewPaymentCondition] = useState('')
  const [isAddingPayment, setIsAddingPayment] = useState(false)
  const [newshippingType, setNewshippingType] = useState('')
  const [isAddingShipping, setIsAddingShipping] = useState(false)

  const cleanCurrency = useCallback((value: any): number => {
    const stringValue = String(value || "")
    const number = Number(stringValue.replace(/[^0-9,-]+/g, "").replace(",", "."))
    return isNaN(number) ? 0 : number
  }, [])

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const formatForInput = useCallback((value: any): string => {
    if (value === null || value === undefined || value === "") {
      return "R$ 0,00"
    }

    const numericValue = typeof value === "string" ? Number(value.replace(/\D/g, "")) / 100 : Number(value)

    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(numericValue)
  }, [])

  const formatDate = useCallback((isoDateString: string | null | undefined): string => {
    if (!isoDateString) return ""
    // Formata a string diretamente para evitar o problema de fuso horário
    return isoDateString.substring(0, 10)
  }, [])

  const formatFileSize = useCallback((bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }, [])

  const handleRemoveExistingAttachment = useCallback(async (attachmentId: number) => {
    if (window.confirm('Tem certeza que deseja remover este anexo?')) {
      try {
        const token = localStorage.getItem('token')
        await axios.delete(
          `${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders/fashion-order/${orderId}/attachments/${attachmentId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )

        setExistingAttachments(prev => prev.filter(att => att.id !== attachmentId))
        alert('Anexo removido com sucesso!')
      } catch (error) {
        console.error('Erro ao remover anexo:', error)
        alert('Erro ao remover anexo. Tente novamente.')
      }
    }
  }, [orderId])

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null
    const MAX_FILE_SIZE_BYTES = 200 * 1024 * 1024 // 200MB

    if (file && file.size > MAX_FILE_SIZE_BYTES) {
      alert("Tamanho do arquivo excede 200MB.")
      setSelectedFile(null)
      setFileName(null)
      setFileUploadStatus("Erro: Arquivo excede 200MB")
      event.target.value = ""
      return
    }

    if (file) {

      setSelectedFile(file)
      setFileName(file.name)
      setFileUploadStatus("")

      const newAttachment: Attachment = {
        id: Date.now(),
        originalName: file.name,
        fileName: file.name,
        filePath: "",
        fileSize: file.size,
        mimeType: file.type,
      }

      setExistingAttachments([newAttachment])
    } else {
      setSelectedFile(null)
      setFileName(null)
      setExistingAttachments([])
    }
  }

  const normalizeProductData = useCallback((product: any): ProductApiData => {
    const sizeQuantities: SizeQuantities = {}

    if (product.items && Array.isArray(product.items)) {
      product.items.forEach((item: FashionOrderProductItemInput) => {
        if (item.size && item.quantity !== undefined) {
          sizeQuantities[item.size] =
            (sizeQuantities[item.size] || 0) + Number(item.quantity)
        }
      })
    }

    const totalItems = Object.values(sizeQuantities).reduce(
      (sum, qty) => sum + qty,
      0
    )

    const price = product.fashionProduct?.price || 0

    const subtotal = product.subtotal ?? totalItems * price

    const colorCount =
      product.colorCount ??
      (product.items
        ? new Set(product.items.map((item: any) => item.colorId)).size
        : 0)

    return {
      ...product,
      sizeQuantities,
      totalItems,
      colorCount,
      items: product.items || [],
      subtotal,
      fashionProduct: product.fashionProduct || {},
    }
  }, [])


  const fetchInitialData = async () => {
    try {
      const [paymentRes, shippingRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders/fashion-order/payment-conditions`),
        axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders/fashion-order/shipping-types`),
      ])

      const paymentConditions = paymentRes.data.map((item: PaymentCondition) => item.name)
      const shippingTypes = shippingRes.data.map((item: { name: string }) => ({
        value: item.name,
        label: item.name,
      }))
      setPaymentOptions(paymentConditions)
      setFreightTypes(shippingTypes)
      return { paymentConditions, shippingTypes }
    } catch (err) {
      console.error('Erro ao buscar dados iniciais:', err)
      setPaymentOptions([])
      setFreightTypes([])
      return { paymentConditions: [], shippingTypes: [] }
    }
  }

  const fetchOrder = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders/fashion-order/${orderId}`)
      return response.data
    } catch (error) {
      console.error('Erro ao buscar o pedido:', error)
      return null
    }
  }

  useEffect(() => {
    const loadData = async () => {
      setLoading(true)

      await fetchInitialData()
      const orderData = await fetchOrder()

      if (orderData) {
        setFormData({
          supplier: {
            id: Number(orderData.supplierId),
            companyName: orderData.supplier?.companyName || '',
          },
          seller: {
            id: orderData.seller?.id || 0,
            name: orderData.seller?.name || '',
          },
          company: {
            id: Number(orderData.companyId),
            companyName: orderData.company?.companyName || '',
          },
          orderType: orderData.orderType || '',
          paymentCondition: orderData.paymentCondition?.name || '',
          shippingType: orderData.shippingType?.name || '',
          trackingCode: orderData.trackingCode || '',
          attachments: orderData.attachments || '',
          finalDateOfDelivery: formatDate(orderData.finalDateOfDelivery),
          startDateOfDelivery: formatDate(orderData.startDateOfDelivery),
          extraInformation: orderData.extraInformation || '',
          quantityItems: orderData.quantityItems || '0',
          quantityPieces: orderData.quantityPieces || '0',
          totalCost: `R$ ${Number(orderData.totalCost || 0)
            .toFixed(2)
            .replace('.', ',')}`,
          deliveryTime: orderData.deliveryTime || '',
          shippingCost: orderData.shippingCost || '',
          maximumBillingAmount: orderData.maximumBillingAmount || '',
        })

        if (orderData.attachments && Array.isArray(orderData.attachments)) {
          setExistingAttachments(orderData.attachments)
        }

        if (orderData.products && Array.isArray(orderData.products)) {
          const normalizedProducts = orderData.products.map((product: any) =>
            normalizeProductData(product)
          )
          setAddedProducts(normalizedProducts)
        } else {
          setAddedProducts([])
        }
      }
      setLoading(false)
    }

    if (orderId) {
      loadData()
    }
  }, [orderId, formatDate, normalizeProductData])

  const handleAddPaymentCondition = async () => {
    if (newPaymentCondition && !paymentOptions.includes(newPaymentCondition)) {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders/fashion-order/payment-conditions`,
          {
            name: newPaymentCondition,
          },
        )
        if (response.status === 201) {
          setPaymentOptions([...paymentOptions, newPaymentCondition])
          setFormData({ ...formData, paymentCondition: newPaymentCondition })
          setNewPaymentCondition('')
          setIsAddingPayment(false)
        }
      } catch (error) {
        console.error('Erro ao salvar a nova condição de pagamento:', error)
      }
    }
  }

  const handleAddshippingType = async () => {
    if (newshippingType && !freightTypes.some((ft) => ft.value === newshippingType)) {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders/fashion-order/shipping-types`,
          {
            name: newshippingType,
          },
        )
        if (response.status === 201) {
          setFreightTypes([...freightTypes, { value: newshippingType, label: newshippingType }])
          setFormData({ ...formData, shippingType: newshippingType })
          setNewshippingType('')
          setIsAddingShipping(false)
        }
      } catch (error) {
        console.error('Erro ao salvar o novo tipo de frete:', error)
      }
    }
  }

  const handleEditSaveOrder = useCallback(async () => {
    try {
      const groupedProductsMap = new Map();

      addedProducts.forEach(product => {
        const productId = product.fashionProduct?.id || product.fashionProductId;

        if (productId === undefined) {
          console.error("Produto sem ID, ignorando...");
          return;
        }

        if (groupedProductsMap.has(productId)) {
          const existingProduct = groupedProductsMap.get(productId)
          existingProduct.items = [...(existingProduct.items || []), ...(product.items || [])]
          existingProduct.totalItems += product.totalItems || 0
          existingProduct.subtotal += product.subtotal || 0
        } else {
          groupedProductsMap.set(productId, { ...product })
        }
      })

      const productsToSend = Array.from(groupedProductsMap.values())

      const payload: EditFashionOrderPayload = {
        orderType: formData.orderType,
        trackingCode: formData.trackingCode,
        extraInformation: formData.extraInformation,
        deliveryTime: formData.deliveryTime,
        maximumBillingAmount: String(cleanCurrency(formData.maximumBillingAmount)),
        quantityItems: formData.quantityItems,
        quantityPieces: formData.quantityPieces,

        totalCost: cleanCurrency(formData.totalCost),
        shippingCost: cleanCurrency(formData.shippingCost),

        finalDateOfDelivery: formData.finalDateOfDelivery ? new Date(formData.finalDateOfDelivery).toISOString() : null,
        startDateOfDelivery: formData.startDateOfDelivery ? new Date(formData.startDateOfDelivery).toISOString() : null,

        supplier: formData.supplier,
        seller: formData.seller,
        company: formData.company,
        shippingType: formData.shippingType,
        paymentCondition: formData.paymentCondition,

        products: productsToSend.map((product) => {
          let itemsToSend = product.items || []

          if ((!itemsToSend || itemsToSend.length === 0) && product.colorCount && product.colorCount > 0) {
            if (product.selectedColors && Array.isArray(product.selectedColors) && product.selectedColors.length > 0) {
              itemsToSend = product.selectedColors.map((colorId: any) => ({
                colorId: colorId,
                size: "N/A",
                quantity: 0,
              }))
            } else if (product.colors && Array.isArray(product.colors) && product.colors.length > 0) {
              itemsToSend = product.colors.map((colorInfo: { id: any }, index: number) => ({
                colorId: typeof colorInfo === "object" ? colorInfo.id : index + 1,
                size: "N/A",
                quantity: 0,
              }))
            } else {
              itemsToSend = Array.from({ length: product.colorCount }, (_, index) => ({
                colorId: index + 1,
                size: "N/A",
                quantity: 0,
              }))
            }
          }

          if (product.sizeQuantities && Object.keys(product.sizeQuantities).length > 0) {
            const sizeItems = Object.entries(product.sizeQuantities).map(([size, quantity]) => ({
              size,
              quantity: Number(quantity) || 0,
              colorId: 1,
            }))

            if (itemsToSend.length > 0) {
              itemsToSend = itemsToSend.map((item: { size: string; quantity: any; colorId: any }) => {
                const sizeMatch = sizeItems.find(si => si.size === item.size)
                return {
                  ...item,
                  quantity: sizeMatch ? sizeMatch.quantity : item.quantity
                }
              })
            } else {
              itemsToSend = sizeItems
            }
          }

          return {
            fashionProductId: product.fashionProduct?.id || product.fashionProductId,
            groupId: product.fashionProduct?.group?.id || product.group?.id,
            discount: product.discount,
            colorCount: product.colorCount,
            totalItems: product.totalItems,
            subtotal: product.subtotal,
            items: itemsToSend,
            colors: product.colors,
            selectedColors: product.selectedColors,
          }
        }),
      }

      if (selectedFile) {
        try {
          setFileUploadStatus("Convertendo arquivo...")
          const base64Data = await fileToBase64(selectedFile)
          payload.attachmentFile = base64Data
          payload.attachmentFileName = selectedFile.name
        } catch (error) {
          console.error('Erro ao converter arquivo para base64:', error)
          setFileUploadStatus("Erro ao processar arquivo")
          alert('Erro ao processar o arquivo anexo')
          return
        }
      }
      const response = await axios.put(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders/fashion-order/${orderId}`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      )
      alert('Pedido atualizado com sucesso!')

      if (selectedFile) {
        setSelectedFile(null)
        setFileName(null)
        setTimeout(() => setFileUploadStatus(""), 3000)
      }

      const updatedOrder = await fetchOrder()
      if (updatedOrder && updatedOrder.attachments && Array.isArray(updatedOrder.attachments)) {
        setExistingAttachments(updatedOrder.attachments)
      }

    } catch (error: any) {
      console.error('Erro ao atualizar o pedido:', error)
      setFileUploadStatus("Erro ao atualizar pedido")
      alert('Erro ao atualizar o pedido')
    }
  }, [formData, addedProducts, orderId, cleanCurrency, selectedFile])

  useEffect(() => {
    if (handleEditSave.current) {
      handleEditSave.current = handleEditSaveOrder
    }
  }, [handleEditSaveOrder, handleEditSave])

  const handleRemoveProduct = (indexToRemove: number) => {
    setAddedProducts((prev) => prev.filter((_, i) => i !== indexToRemove))
    setOpenProductIndex(null)
  }

  const handleUpdateProduct = (oldProduct: any, updatedProduct: any) => {
    if (openProductIndex !== null) {
      const normalizedProduct = normalizeProductData(updatedProduct)

      setAddedProducts((prevProducts) => {
        const newProducts = [...prevProducts]
        const existingProduct = newProducts[openProductIndex]

        newProducts[openProductIndex] = {
          ...existingProduct,
          ...normalizedProduct,
        }

        return newProducts
      })

      setOpenProductIndex(null)
    } else {
      console.warn("EditFashionOrder - openProductIndex é null, não foi possível atualizar produto.")
    }
  }

  const handleNewProductAdded = (newProduct: ProductApiData) => {
    const normalizedProduct = normalizeProductData(newProduct)

    setAddedProducts((prevProducts) => [...prevProducts, normalizedProduct])
    setShowAddProduct(false)
    setOpenProductIndex(null)
  }

  useEffect(() => {
    let totalItemsCount = 0
    let totalPiecesSum = 0
    let totalCostSum = 0
    let totalDiscountValue = 0

    addedProducts.forEach((product) => {
      totalItemsCount += 1

      const productTotalItems = product.sizeQuantities
        ? Object.values(product.sizeQuantities).reduce((sum, qty) => sum + qty, 0)
        : product.totalItems || 0

      totalPiecesSum += productTotalItems

      const subtotalWithoutDiscount = product.subtotal || 0

      let discountValue = 0
      if (product.discount && product.discount > 0) {
        const originalSubtotal = subtotalWithoutDiscount / (1 - product.discount / 100)
        discountValue = calculateDiscountValue(originalSubtotal, product.discount)
      }

      totalDiscountValue += discountValue
      totalCostSum += subtotalWithoutDiscount
    })

    setFormData((prevState) => ({
      ...prevState,
      quantityItems: String(totalItemsCount),
      quantityPieces: String(totalPiecesSum),
      totalCost: formatCurrency(totalCostSum),
    }))
  }, [addedProducts])

  const handleSupplierChange = (supplier: CompanyItemType) => {
    setFormData((prevState) => ({ ...prevState, supplier }))
  }

  const handleCompanyChange = (company: CompanyItemType) => {
    setFormData((prevState) => ({ ...prevState, company }))
  }

  const handleSellerChange = (seller: UserItemType) => {
    setFormData((prevState) => ({ ...prevState, seller }))
  }

  const getSafeValue = (value: any): string => {
    if (typeof value === 'object' && value !== null) {
      if ('id' in value && 'name' in value) {
        return value.name || '-'
      }
      return '-'
    }
    return String(value) || '-'
  }

  const calculateDiscountMonetaryValue = (product: ProductApiData): string => {
    if (!product.discount || product.discount <= 0 || !product.subtotal) {
      return "-"
    }

    const originalSubtotal = product.subtotal / (1 - product.discount / 100)

    const discountValue = calculateDiscountValue(originalSubtotal, product.discount)

    return formatCurrency(discountValue)
  }

  return (
    <>
      <BreadCrumble>
        <FirstText>Pedidos fashion</FirstText>
        <SecondText>&gt; Editar pedido</SecondText>
      </BreadCrumble>

      <Card>
        <FormGrid>
          <FormGroup>
            <CompanySelect
              label='Fornecedor'
              apiUrl={`${process.env.REACT_APP_GATEWAY_BASE_URL}/certificates/certificate/utils/companies`}
              displayField='companyName'
              queryParams={{ companyType: 2 }}
              value={formData.supplier}
              onChange={handleSupplierChange}
            />
            <InfoLink>
              <Info size={16} />
              <span>Mais informações</span>
            </InfoLink>
          </FormGroup>

          <FormGroup>
            <VendorSelect
              label='Vendedor'
              apiUrl={`${process.env.REACT_APP_GATEWAY_BASE_URL}/backend/user/findAll`}
              displayField='name'
              value={formData.seller}
              onChange={handleSellerChange}
            />
            <InfoLink>
              <Info size={16} />
              <span>Mais informações</span>
            </InfoLink>
          </FormGroup>

          <FormGroup>
            <CompanySelect
              label='Cliente'
              apiUrl={`${process.env.REACT_APP_GATEWAY_BASE_URL}/certificates/certificate/utils/companies`}
              displayField='companyName'
              queryParams={{ companyType: 1 }}
              value={formData.company}
              onChange={handleCompanyChange}
            />
            <InfoLink>
              <Info size={16} />
              <span>Mais informações</span>
            </InfoLink>
          </FormGroup>
        </FormGrid>

        <Separator />

        <CollapsibleHeader onClick={() => setIsDetailsOpen(!isDetailsOpen)}>
          <Title>Detalhes do pedido</Title>
          <Button>{isDetailsOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}</Button>
        </CollapsibleHeader>

        {isDetailsOpen && (
          <>
            <FormGrid style={{ marginTop: '16px' }}>
              <FormGroup>
                <Label>Tipo de pedido</Label>
                <Select
                  value={formData.orderType}
                  onChange={(e) => setFormData({ ...formData, orderType: e.target.value })}
                >
                  <option value=''>Selecione o tipo de pedido</option>
                  <option value='Venda'>Venda</option>
                  <option value='Reposição'>Reposição</option>
                </Select>
              </FormGroup>

              <FormGroup>
                <Label>Data inicio da entrega</Label>
                <InputText
                  type='date'
                  value={formData.startDateOfDelivery}
                  max="9999-12-31"
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      startDateOfDelivery: e.target.value,
                    })
                  }
                />
              </FormGroup>

              <FormGroup>
                <Label>Data final da entrega</Label>
                <InputText
                  type='date'
                  value={formData.finalDateOfDelivery}
                  max="9999-12-31"
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      finalDateOfDelivery: e.target.value,
                    })
                  }
                />
              </FormGroup>
            </FormGrid>

            <FormGrid>
              <FormGroup>
                <Label>Condição de pagamento</Label>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  {!isAddingPayment ? (
                    <Select
                      name='paymentCondition'
                      value={formData.paymentCondition}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          paymentCondition: e.target.value,
                        })
                      }
                      style={{ flexGrow: 1 }}
                    >
                      <option value="">Selecione a condição de pagamento</option>
                      {paymentOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </Select>
                  ) : (
                    <InputText
                      type='text'
                      placeholder='Digite a nova condição'
                      value={newPaymentCondition}
                      onChange={(e) => setNewPaymentCondition(e.target.value)}
                      onBlur={handleAddPaymentCondition}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleAddPaymentCondition()
                          setIsAddingPayment(false)
                        }
                      }}
                      autoFocus
                      style={{ flexGrow: 1 }}
                    />
                  )}
                  <Plus
                    size={16}
                    onClick={() => setIsAddingPayment(!isAddingPayment)}
                    style={{ cursor: 'pointer', minWidth: '16px' }}
                  />
                </div>
              </FormGroup>

              <FormGroup>
                <Label>Tipo de frete</Label>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  {!isAddingShipping ? (
                    <Select
                      value={formData.shippingType}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          shippingType: e.target.value,
                        })
                      }
                      style={{ flexGrow: 1 }}
                    >
                      <option value=''>Selecione o tipo de frete</option>
                      {freightTypes.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  ) : (
                    <InputText
                      type='text'
                      placeholder='Digite o novo tipo de frete'
                      value={newshippingType}
                      onChange={(e) => setNewshippingType(e.target.value)}
                      onBlur={handleAddshippingType}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleAddshippingType()
                          setIsAddingShipping(false)
                        }
                      }}
                      autoFocus
                      style={{ flexGrow: 1 }}
                    />
                  )}
                  <Plus
                    size={16}
                    onClick={() => setIsAddingShipping(!isAddingShipping)}
                    style={{ cursor: 'pointer', minWidth: '16px' }}
                  />
                </div>
              </FormGroup>

              <FormGroup>
                <Label>Valor do frete</Label>
                <InputText
                  type="text"
                  value={formatForInput(formData.shippingCost)}
                  onChange={(e) => {
                    const cleanedValue = e.target.value.replace(/\D/g, "")
                    setFormData({ ...formData, shippingCost: cleanedValue })
                  }}
                  placeholder="Digite o valor do frete"
                />
              </FormGroup>
            </FormGrid>

            <FormGrid>
              <FormGroup>
                <Label>Código de rastreio</Label>
                <InputText
                  type="text"
                  value={formData.trackingCode}
                  onChange={(e) => setFormData({ ...formData, trackingCode: e.target.value })}
                  placeholder="Digite o código de rastreio"
                />
              </FormGroup>

              <FormGroup>
                <Label>Anexo</Label>
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
                >
                  <FileUploadContainer style={{ flexGrow: 1 }}>
                    <FileUploadLabel htmlFor='file-upload'>
                      Escolher arquivo
                    </FileUploadLabel>
                    <input
                      id='file-upload'
                      type='file'
                      style={{ display: 'none' }}
                      onChange={handleFileChange}
                      accept='*/*'
                    />
                    <FileNameText>
                      {selectedFile
                        ? selectedFile.name
                        : existingAttachments.length > 0
                          ? existingAttachments[0].originalName
                          : 'Nenhum arquivo escolhido'}
                    </FileNameText>
                  </FileUploadContainer>

                  {(selectedFile || existingAttachments.length > 0) && (
                    <img
                      src={cross}
                      alt='Remover anexo'
                      style={{
                        cursor: 'pointer',
                        minWidth: '10px',
                        width: '10px',
                        height: '10px',
                        filter: 'brightness(0)',
                      }}
                      onClick={() => {
                        if (selectedFile) {
                          setSelectedFile(null)
                          setFileName(null)
                          setFileUploadStatus('')

                          const fileInput = document.getElementById(
                            'file-upload',
                          ) as HTMLInputElement
                          if (fileInput) {
                            fileInput.value = ''
                          }
                        } else if (existingAttachments.length > 0) {
                          handleRemoveExistingAttachment(
                            existingAttachments[0].id,
                          )
                        }
                      }}
                    />
                  )}
                </div>
              </FormGroup>
              <FormGroup>
                <Label>Valor máximo de faturamento</Label>
                <InputText
                  type="text"
                  value={formatForInput(formData.maximumBillingAmount)}
                  onChange={(e) => {
                    const cleanedValue = e.target.value.replace(/\D/g, "")
                    setFormData({
                      ...formData,
                      maximumBillingAmount: cleanedValue,
                    })
                  }}
                  className="w-full rounded bg-gray-100 p-2"
                  placeholder="Digite o valor máximo"
                />
              </FormGroup>
            </FormGrid>
            <FormGrid>
              <FormGroup>
                <Label>Informações adicionais</Label>
                <InputText
                  type='text'
                  value={formData.extraInformation}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      extraInformation: e.target.value,
                    })
                  }
                />
              </FormGroup>
            </FormGrid>
          </>
        )}

        <Separator />

        <Summary>
          <SummaryItem>
            <SummaryLabel>Quantidade de itens:</SummaryLabel>
            <SummaryValue>{formData.quantityItems}</SummaryValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryLabel>Quantidade de peças:</SummaryLabel>
            <SummaryValue>{formData.quantityPieces}</SummaryValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryLabel>Total:</SummaryLabel>
            <SummaryValue>{formData.totalCost}</SummaryValue>
          </SummaryItem>
        </Summary>
      </Card>

      <>
        {Array.isArray(addedProducts) && addedProducts.length > 0 && (
          <>
            {addedProducts.map((product, index) => {
              const productName = product.fashionProduct?.name || product.name || "-"
              const familyName = product.fashionProduct?.group?.family?.name || product.family?.name || "-"
              const groupName = product.fashionProduct?.group?.name || product.group?.name || "-"

              const priceValue =
                typeof product.fashionProduct?.price === "number"
                  ? `R$ ${product.fashionProduct.price.toFixed(2).replace(".", ",")}`
                  : "-"

              const subtotalValue =
                typeof product.subtotal === "number" ? formatCurrency(product.subtotal) : "-"

              const currentTotalItems = product.sizeQuantities
                ? Object.values(product.sizeQuantities).reduce((sum, qty) => sum + qty, 0)
                : product.totalItems || 0

              const productFields = [
                { label: 'Produto', value: productName, showIcon: true },
                { label: 'Família', value: familyName, showIcon: true },
                {
                  label: "Cores",
                  value: product.colorCount ?? "-",
                  showIcon: true,
                },
                { label: "Precificação", value: priceValue, showIcon: true },
                {
                  label: "Quantidade de peças",
                  value: currentTotalItems ?? "-",
                  showIcon: true,
                },
                {
                  label: "Desconto",
                  value: calculateDiscountMonetaryValue(product),
                  showIcon: true,
                },
                { label: "Subtotal", value: subtotalValue, showIcon: false },
              ]

              return (
                <Card key={product.fashionProductId || index}>
                  <ProductShort>
                    <ProductDiv>
                      {productFields.map(({ label, value, showIcon }, i) => (
                        <ShortLabel key={i}>
                          <span style={{ fontWeight: "normal" }}>{label}:</span> {getSafeValue(value)}{" "}
                          {showIcon && (
                            <img style={{ marginLeft: "0.5rem" }} src={rectangle || "/placeholder.svg"} alt="" />
                          )}
                        </ShortLabel>
                      ))}
                      <ActionsDiv className='flex items-center gap-2'>
                        <img
                          src={trash}
                          alt='Remover'
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleRemoveProduct(index)}
                        />
                        <Button
                          onClick={() => {
                            setOpenProductIndex(openProductIndex === index ? null : index)
                          }}
                        >
                          {openProductIndex === index ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                        </Button>
                      </ActionsDiv>
                    </ProductDiv>
                  </ProductShort>

                  {openProductIndex === index && (
                    <AddProductForm
                      order={addedProducts}
                      setOrder={setAddedProducts}
                      product={product}
                      onClose={() => {
                        setOpenProductIndex(null)
                      }}
                      onProductUpdated={handleUpdateProduct}
                      onRemoveProduct={handleRemoveProduct}
                      isEditing={true}
                      onProductAdded={handleNewProductAdded}
                    />
                  )}
                </Card>
              )
            })}
          </>
        )}
      </>

      <AddProductContainer>
        {showAddProduct && (
          <AddProductForm
            order={addedProducts}
            setOrder={setAddedProducts}
            product={undefined}
            onClose={() => setShowAddProduct(false)}
            onRemoveProduct={handleRemoveProduct}
            onProductUpdated={handleUpdateProduct}
            isEditing={false}
            onProductAdded={handleNewProductAdded}
          />
        )}
        <AddButton
          onClick={() => {
            setShowAddProduct(!showAddProduct)
            setOpenProductIndex(null)
          }}
        >
          <AddButtonImg src={plus} alt='Adicionar' />
          <AddButtonText>Adicionar produto</AddButtonText>
        </AddButton>
      </AddProductContainer>
    </>
  )
}

export default EditFashionOrder